import { css } from 'astroturf';
import React from 'react';
import { ArrowRight, ArrowBottom } from "react-bytesize-icons";
import { CheatsheetCard } from "./cheatsheet-card";
import { Section, PropertyRow } from "./cheatsheet-elements";

const styles = css`
  .twoColumnGrid {
    display: grid;
    grid-template-columns: repeat(2, minmax(0, 1fr));
    gap: 0.25rem;
  }

  .axesGrid {
    display: grid;
    grid-template-columns: repeat(2, minmax(0, 1fr));
    gap: 0.5rem;
    font-size: 10px;
    line-height: 1.2;
    color: var(--sheet-slate-300);
  }

  .axisColumn {
    display: flex;
    flex-direction: column;
    align-items: center;
  }

  .axisLabelUnderlined {
    font-size: 11px;
    line-height: 1.25;
    font-weight: 600;
    color: var(--sheet-cyan-300);
    margin-bottom: 0.25rem;
    text-decoration-line: underline;
    text-decoration-color: var(--sheet-slate-400);
    text-underline-offset: 2px;
  }

  .axisRowWithMargin {
    display: flex;
    align-items: center;
    gap: 0.25rem;
    margin-bottom: 0.125rem;
  }

  .axisIcon {
    height: 0.75rem;
    width: 0.75rem;
    color: var(--sheet-emerald-300);
  }

  .axisHint {
    font-size: 9px;
    line-height: 1.2;
  }

  .axisRow {
    display: flex;
    align-items: center;
    gap: 0.25rem;
  }

  .axisLabel {
    font-size: 11px;
    line-height: 1.25;
    font-weight: 600;
    color: var(--sheet-cyan-300);
    margin-bottom: 0.25rem;
  }

  .directionGrid {
    display: grid;
    grid-template-columns: repeat(4, minmax(0, 1fr));
    gap: 0.25rem;
  }

  .directionRowDemo {
    display: flex;
    flex-direction: row;
    gap: 0.125rem;
    background-color: var(--sheet-slate-900-50);
    border-radius: 0.25rem;
    padding: 0.375rem;
    border-width: 1px;
    border-style: solid;
    border-color: var(--sheet-slate-600-30);
  }

  .directionBadge {
    width: 0.875rem;
    height: 0.875rem;
    background-color: var(--sheet-cyan-500);
    border-radius: 1rem;
    font-size: 9px;
    line-height: 1.2;
    color: var(--sheet-slate-900);
    display: flex;
    align-items: center;
    justify-content: center;
    font-weight: 500;
  }

  .tinyCaptionUnderlined {
    font-size: 10px;
    line-height: 1.2;
    color: var(--sheet-slate-400);
    margin-top: 0.0625rem;
    text-align: center;
    text-decoration-line: underline;
    text-decoration-color: var(--sheet-slate-400);
    text-underline-offset: 2px;
  }

  .directionColumnDemo {
    display: flex;
    flex-direction: column;
    gap: 0.125rem;
    background-color: var(--sheet-slate-900-50);
    border-radius: 0.25rem;
    padding: 0.375rem;
    border-width: 1px;
    border-style: solid;
    border-color: var(--sheet-slate-600-30);
  }

  .tinyCaption {
    font-size: 10px;
    line-height: 1.2;
    color: var(--sheet-slate-400);
    margin-top: 0.0625rem;
    text-align: center;
  }

  .directionRowReverseDemo {
    display: flex;
    flex-direction: row-reverse;
    gap: 0.125rem;
    background-color: var(--sheet-slate-900-50);
    border-radius: 0.25rem;
    padding: 0.375rem;
    border-width: 1px;
    border-style: solid;
    border-color: var(--sheet-slate-600-30);
  }

  .directionColumnReverseDemo {
    display: flex;
    flex-direction: column-reverse;
    gap: 0.125rem;
    background-color: var(--sheet-slate-900-50);
    border-radius: 0.25rem;
    padding: 0.375rem;
    border-width: 1px;
    border-style: solid;
    border-color: var(--sheet-slate-600-30);
  }

  .justifyGrid {
    display: grid;
    grid-template-columns: repeat(3, minmax(0, 1fr));
    gap: 0.25rem;
  }

  .justifyStartDemo {
    display: flex;
    justify-content: flex-start;
    gap: 0.125rem;
    background-color: var(--sheet-slate-900-50);
    border-radius: 0.25rem;
    padding: 0.375rem;
    border-width: 1px;
    border-style: solid;
    border-color: var(--sheet-slate-600-30);
  }

  .emeraldTile {
    width: 0.75rem;
    height: 0.75rem;
    background-color: var(--sheet-emerald-500);
    border-radius: 0.125rem;
  }

  .justifyEndDemo {
    display: flex;
    justify-content: flex-end;
    gap: 0.125rem;
    background-color: var(--sheet-slate-900-50);
    border-radius: 0.25rem;
    padding: 0.375rem;
    border-width: 1px;
    border-style: solid;
    border-color: var(--sheet-slate-600-30);
  }

  .justifyCenterDemo {
    display: flex;
    justify-content: center;
    gap: 0.125rem;
    background-color: var(--sheet-slate-900-50);
    border-radius: 0.25rem;
    padding: 0.375rem;
    border-width: 1px;
    border-style: solid;
    border-color: var(--sheet-slate-600-30);
  }

  .justifyBetweenDemo {
    display: flex;
    justify-content: space-between;
    background-color: var(--sheet-slate-900-50);
    border-radius: 0.25rem;
    padding: 0.375rem;
    border-width: 1px;
    border-style: solid;
    border-color: var(--sheet-slate-600-30);
  }

  .justifyAroundDemo {
    display: flex;
    justify-content: space-around;
    background-color: var(--sheet-slate-900-50);
    border-radius: 0.25rem;
    padding: 0.375rem;
    border-width: 1px;
    border-style: solid;
    border-color: var(--sheet-slate-600-30);
  }

  .justifyEvenlyDemo {
    display: flex;
    justify-content: space-evenly;
    background-color: var(--sheet-slate-900-50);
    border-radius: 0.25rem;
    padding: 0.375rem;
    border-width: 1px;
    border-style: solid;
    border-color: var(--sheet-slate-600-30);
  }

  .alignGrid {
    display: grid;
    grid-template-columns: repeat(5, minmax(0, 1fr));
    gap: 0.25rem;
  }

  .alignStretchDemo {
    display: flex;
    align-items: stretch;
    gap: 0.125rem;
    background-color: var(--sheet-slate-900-50);
    border-radius: 0.25rem;
    padding: 0.375rem;
    height: 2.25rem;
    border-width: 1px;
    border-style: solid;
    border-color: var(--sheet-slate-600-30);
  }

  .cyanBarStretch {
    width: 0.625rem;
    background-color: var(--sheet-cyan-500);
    border-radius: 0.125rem;
  }

  .tinyCaptionAltUnderlined {
    font-size: 9px;
    line-height: 1.2;
    color: var(--sheet-slate-400);
    margin-top: 0.0625rem;
    text-align: center;
    text-decoration-line: underline;
    text-decoration-color: var(--sheet-slate-400);
    text-underline-offset: 2px;
  }

  .alignStartDemo {
    display: flex;
    align-items: flex-start;
    gap: 0.125rem;
    background-color: var(--sheet-slate-900-50);
    border-radius: 0.25rem;
    padding: 0.375rem;
    height: 2.25rem;
    border-width: 1px;
    border-style: solid;
    border-color: var(--sheet-slate-600-30);
  }

  .cyanBarShort {
    width: 0.625rem;
    height: 0.625rem;
    background-color: var(--sheet-cyan-500);
    border-radius: 0.125rem;
  }

  .cyanBarTall {
    width: 0.625rem;
    height: 1rem;
    background-color: var(--sheet-cyan-500);
    border-radius: 0.125rem;
  }

  .cyanBarMedium {
    width: 0.625rem;
    height: 0.75rem;
    background-color: var(--sheet-cyan-500);
    border-radius: 0.125rem;
  }

  .tinyCaptionAlt {
    font-size: 9px;
    line-height: 1.2;
    color: var(--sheet-slate-400);
    margin-top: 0.0625rem;
    text-align: center;
  }

  .alignEndDemo {
    display: flex;
    align-items: flex-end;
    gap: 0.125rem;
    background-color: var(--sheet-slate-900-50);
    border-radius: 0.25rem;
    padding: 0.375rem;
    height: 2.25rem;
    border-width: 1px;
    border-style: solid;
    border-color: var(--sheet-slate-600-30);
  }

  .alignCenterDemo {
    display: flex;
    align-items: center;
    gap: 0.125rem;
    background-color: var(--sheet-slate-900-50);
    border-radius: 0.25rem;
    padding: 0.375rem;
    height: 2.25rem;
    border-width: 1px;
    border-style: solid;
    border-color: var(--sheet-slate-600-30);
  }

  .alignBaselineDemo {
    display: flex;
    align-items: baseline;
    gap: 0.125rem;
    background-color: var(--sheet-slate-900-50);
    border-radius: 0.25rem;
    padding: 0.375rem;
    height: 2.25rem;
    border-width: 1px;
    border-style: solid;
    border-color: var(--sheet-slate-600-30);
    position: relative;
  }

  .baselineGuide {
    position: absolute;
    top: 1rem;
    left: 0.25rem;
    right: 0.25rem;
    height: 1px;
    background-color: #ffffff;
  }

  .cyanBarShortOffset {
    width: 0.625rem;
    height: 0.625rem;
    background-color: var(--sheet-cyan-500);
    border-radius: 0.125rem;
    margin-top: 0.375rem;
  }

  .cyanBarMediumOffset {
    width: 0.625rem;
    height: 0.75rem;
    background-color: var(--sheet-cyan-500);
    border-radius: 0.125rem;
    margin-top: 0.25rem;
  }

  .gapDemoRow {
    display: flex;
    gap: 0.25rem;
    background-color: var(--sheet-slate-900-50);
    border-radius: 0.25rem;
    padding: 0.375rem;
    border-width: 1px;
    border-style: solid;
    border-color: var(--sheet-slate-600-30);
  }

  .gapDemoWrap {
    display: flex;
    flex-wrap: wrap;
    gap: 0.5rem;
    row-gap: 0.125rem;
    background-color: var(--sheet-slate-900-50);
    border-radius: 0.25rem;
    padding: 0.375rem;
    border-width: 1px;
    border-style: solid;
    border-color: var(--sheet-slate-600-30);
    height: 2.25rem;
  }

  .wrapNowrapDemo {
    display: flex;
    flex-wrap: nowrap;
    gap: 0.125rem;
    background-color: var(--sheet-slate-900-50);
    border-radius: 0.25rem;
    padding: 0.375rem;
    border-width: 1px;
    border-style: solid;
    border-color: var(--sheet-slate-600-30);
    overflow: hidden;
  }

  .emeraldTileNoShrink {
    width: 0.75rem;
    height: 0.75rem;
    background-color: var(--sheet-emerald-500);
    border-radius: 0.125rem;
    flex-shrink: 0;
  }

  .wrapDemo {
    display: flex;
    flex-wrap: wrap;
    gap: 0.125rem;
    background-color: var(--sheet-slate-900-50);
    border-radius: 0.25rem;
    padding: 0.375rem;
    height: 2.5rem;
    border-width: 1px;
    border-style: solid;
    border-color: var(--sheet-slate-600-30);
  }

  .wrapReverseDemo {
    display: flex;
    flex-wrap: wrap-reverse;
    gap: 0.125rem;
    background-color: var(--sheet-slate-900-50);
    border-radius: 0.25rem;
    padding: 0.375rem;
    height: 2.5rem;
    border-width: 1px;
    border-style: solid;
    border-color: var(--sheet-slate-600-30);
  }

  .flexSizingRow {
    display: flex;
    gap: 0.125rem;
    background-color: var(--sheet-slate-900-50);
    border-radius: 0.25rem;
    padding: 0.375rem;
    border-width: 1px;
    border-style: solid;
    border-color: var(--sheet-slate-600-30);
  }

  .flexSizingTileEmerald {
    height: 1.5rem;
    background-color: var(--sheet-emerald-500);
    border-radius: 0.125rem;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 9px;
    line-height: 1.2;
    color: var(--sheet-slate-900);
    font-weight: 500;
  }

  .flexSizingTileCyan {
    height: 1.5rem;
    background-color: var(--sheet-cyan-500);
    border-radius: 0.125rem;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 9px;
    line-height: 1.2;
    color: var(--sheet-slate-900);
    font-weight: 500;
  }
`;

export function FlexCheatsheet({ withBleed = false }) {
  return (
    <CheatsheetCard title="CSS Flexbox" icon="flex" withBleed={withBleed}>
      <div className={styles.twoColumnGrid}>
        <div>
          <Section title="Layout / Align Items">
            <PropertyRow property="display" values={["flex", "inline-flex"]} />
             <PropertyRow
              property="flex-flow"
              values={["<direction> <wrap>"]}
              defaultValue="row nowrap"
            />
            <PropertyRow
              property="flex-direction"
              values={["row", "row-reverse", "column", "column-reverse"]}
              defaultValue="row"
            />
            <PropertyRow
              property="justify-content"
              values={["flex-start", "flex-end", "center", "space-between", "space-around", "space-evenly"]}
              defaultValue="flex-start"
            />
            <PropertyRow
              property="align-items"
              values={["stretch", "flex-start", "flex-end", "center", "baseline"]}
              defaultValue="stretch"
            />
            <PropertyRow property="gap" values={["<length>", "<row> <column>"]} defaultValue="0" />
            <PropertyRow property="flex-wrap" values={["nowrap", "wrap", "wrap-reverse"]} defaultValue="nowrap" />
          </Section>

          <Section title="Align Lines / Content">
            <PropertyRow
              property="align-content"
              values={["stretch", "flex-start", "flex-end", "center", "space-between", "space-around", "space-evenly"]}
              defaultValue="stretch"
              description="Aligns wrapped lines, not individual items"
            />
            <PropertyRow
              property="place-content"
              values={["<align-content> <justify-content>", "center space-between"]}
              description="Shorthand for line/content alignment"
            />
          </Section>

          <Section title="Item Properties">
            <PropertyRow
              property="flex"
              values={["<grow> <shrink> <basis>", "auto"]}
              description="flex: 1 = flex: 1 1 0%"
            />
            <PropertyRow property="flex-grow" values={["<number>"]} defaultValue="0" />
            <PropertyRow property="flex-shrink" values={["<number>"]} defaultValue="1" />
            <PropertyRow property="flex-basis" values={["auto", "<length>", "<percentage>"]} defaultValue="auto" />
            <PropertyRow
              property="align-self"
              values={["auto", "flex-start", "flex-end", "center", "stretch"]}
              defaultValue="auto"
            />
            <PropertyRow property="order" values={["<integer>"]} defaultValue="0" />
            <PropertyRow
              property="margin"
              values={["<length>", "auto"]}
              defaultValue="0"
              description="Push item away from siblings"
            />
          </Section>
        </div>

        <div>
          <Section title="Axes">
            <div className={styles.axesGrid}>
              <div className={styles.axisColumn}>
                <div className={styles.axisLabelUnderlined}>
                  row (default)
                </div>
                <div className={styles.axisRowWithMargin}>
                  <ArrowRight className={styles.axisIcon} strokeWidth={2.25} />
                  <span className={styles.axisHint}>justify-content</span>
                </div>
                <div className={styles.axisRow}>
                  <ArrowBottom className={styles.axisIcon} strokeWidth={2.25} />
                  <span className={styles.axisHint}>align-items</span>
                </div>
              </div>
              <div className={styles.axisColumn}>
                <div className={styles.axisLabel}>column</div>
                <div className={styles.axisRowWithMargin}>
                  <ArrowBottom className={styles.axisIcon} strokeWidth={2.25} />
                  <span className={styles.axisHint}>justify-content</span>
                </div>
                <div className={styles.axisRow}>
                  <ArrowRight className={styles.axisIcon} strokeWidth={2.25} />
                  <span className={styles.axisHint}>align-items</span>
                </div>
              </div>
            </div>
          </Section>

          <Section title="flex-direction">
            <div className={styles.directionGrid}>
              <div>
                <div className={styles.directionRowDemo}>
                  <div className={styles.directionBadge}>
                    1
                  </div>
                  <div className={styles.directionBadge}>
                    2
                  </div>
                </div>
                <div className={styles.tinyCaptionUnderlined}>
                  row
                </div>
              </div>
              <div>
                <div className={styles.directionColumnDemo}>
                  <div className={styles.directionBadge}>
                    1
                  </div>
                  <div className={styles.directionBadge}>
                    2
                  </div>
                </div>
                <div className={styles.tinyCaption}>column</div>
              </div>
              <div>
                <div className={styles.directionRowReverseDemo}>
                  <div className={styles.directionBadge}>
                    1
                  </div>
                  <div className={styles.directionBadge}>
                    2
                  </div>
                </div>
                <div className={styles.tinyCaption}>row-reverse</div>
              </div>
              <div>
                <div className={styles.directionColumnReverseDemo}>
                  <div className={styles.directionBadge}>
                    1
                  </div>
                  <div className={styles.directionBadge}>
                    2
                  </div>
                </div>
                <div className={styles.tinyCaption}>col-reverse</div>
              </div>
            </div>
          </Section>

          <Section title="justify-content">
            <div className={styles.justifyGrid}>
              <div>
                <div className={styles.justifyStartDemo}>
                  <div className={styles.emeraldTile} />
                  <div className={styles.emeraldTile} />
                  <div className={styles.emeraldTile} />
                </div>
                <div className={styles.tinyCaptionUnderlined}>
                  flex-start
                </div>
              </div>
              <div>
                <div className={styles.justifyEndDemo}>
                  <div className={styles.emeraldTile} />
                  <div className={styles.emeraldTile} />
                  <div className={styles.emeraldTile} />
                </div>
                <div className={styles.tinyCaption}>flex-end</div>
              </div>
              <div>
                <div className={styles.justifyCenterDemo}>
                  <div className={styles.emeraldTile} />
                  <div className={styles.emeraldTile} />
                  <div className={styles.emeraldTile} />
                </div>
                <div className={styles.tinyCaption}>center</div>
              </div>
              <div>
                <div className={styles.justifyBetweenDemo}>
                  <div className={styles.emeraldTile} />
                  <div className={styles.emeraldTile} />
                  <div className={styles.emeraldTile} />
                </div>
                <div className={styles.tinyCaption}>space-between</div>
              </div>
              <div>
                <div className={styles.justifyAroundDemo}>
                  <div className={styles.emeraldTile} />
                  <div className={styles.emeraldTile} />
                  <div className={styles.emeraldTile} />
                </div>
                <div className={styles.tinyCaption}>space-around</div>
              </div>
              <div>
                <div className={styles.justifyEvenlyDemo}>
                  <div className={styles.emeraldTile} />
                  <div className={styles.emeraldTile} />
                  <div className={styles.emeraldTile} />
                </div>
                <div className={styles.tinyCaption}>space-evenly</div>
              </div>
            </div>
          </Section>

          <Section title="align-items">
            <div className={styles.alignGrid}>
              <div>
                <div className={styles.alignStretchDemo}>
                  <div className={styles.cyanBarStretch} />
                  <div className={styles.cyanBarStretch} />
                  <div className={styles.cyanBarStretch} />
                </div>
                <div className={styles.tinyCaptionAltUnderlined}>
                  stretch
                </div>
              </div>
              <div>
                <div className={styles.alignStartDemo}>
                  <div className={styles.cyanBarShort} />
                  <div className={styles.cyanBarTall} />
                  <div className={styles.cyanBarMedium} />
                </div>
                <div className={styles.tinyCaptionAlt}>flex-start</div>
              </div>
              <div>
                <div className={styles.alignEndDemo}>
                  <div className={styles.cyanBarShort} />
                  <div className={styles.cyanBarTall} />
                  <div className={styles.cyanBarMedium} />
                </div>
                <div className={styles.tinyCaptionAlt}>flex-end</div>
              </div>
              <div>
                <div className={styles.alignCenterDemo}>
                  <div className={styles.cyanBarShort} />
                  <div className={styles.cyanBarTall} />
                  <div className={styles.cyanBarMedium} />
                </div>
                <div className={styles.tinyCaptionAlt}>center</div>
              </div>

              <div>
                <div className={styles.alignBaselineDemo}>
                  <div className={styles.baselineGuide} />
                  <div className={styles.cyanBarShortOffset} />
                  <div className={styles.cyanBarTall} />
                  <div className={styles.cyanBarMediumOffset} />
                </div>
                <div className={styles.tinyCaptionAlt}>baseline</div>
              </div>
            </div>
          </Section>

          <Section title="gap">
            <div className={styles.twoColumnGrid}>
              <div>
                <div className={styles.gapDemoRow}>
                  <div className={styles.emeraldTile} />
                  <div className={styles.emeraldTile} />
                  <div className={styles.emeraldTile} />
                </div>
                <div className={styles.tinyCaption}>gap: 4px</div>
              </div>
              <div>
                <div className={styles.gapDemoWrap}>
                  <div className={styles.emeraldTile} />
                  <div className={styles.emeraldTile} />
                  <div className={styles.emeraldTile} />
                  <div className={styles.emeraldTile} />
                  <div className={styles.emeraldTile} />
                  <div className={styles.emeraldTile} />
                  <div className={styles.emeraldTile} />
                </div>
                <div className={styles.tinyCaption}>gap: 8px 4px</div>
              </div>
            </div>
          </Section>

          <Section title="flex-wrap">
            <div className={styles.justifyGrid}>
              <div>
                <div className={styles.wrapNowrapDemo}>
                  <div className={styles.emeraldTileNoShrink} />
                  <div className={styles.emeraldTileNoShrink} />
                  <div className={styles.emeraldTileNoShrink} />
                  <div className={styles.emeraldTileNoShrink} />
                  <div className={styles.emeraldTile} />
                  <div className={styles.emeraldTile} />
                </div>
                <div className={styles.tinyCaptionUnderlined}>
                  nowrap
                </div>
              </div>
              <div>
                <div className={styles.wrapDemo}>
                  <div className={styles.emeraldTile} />
                  <div className={styles.emeraldTile} />
                  <div className={styles.emeraldTile} />
                  <div className={styles.emeraldTile} />
                  <div className={styles.emeraldTile} />
                  <div className={styles.emeraldTile} />
                  <div className={styles.emeraldTile} />
                </div>
                <div className={styles.tinyCaption}>wrap</div>
              </div>
              <div>
                <div className={styles.wrapReverseDemo}>
                  <div className={styles.emeraldTile} />
                  <div className={styles.emeraldTile} />
                  <div className={styles.emeraldTile} />
                  <div className={styles.emeraldTile} />
                  <div className={styles.emeraldTile} />
                  <div className={styles.emeraldTile} />
                  <div className={styles.emeraldTile} />
                </div>
                <div className={styles.tinyCaption}>wrap-reverse</div>
              </div>
            </div>
          </Section>

          <Section title="Item flex sizing">
            <div className={styles.flexSizingRow}>
              <div
                style={{ flex: "1" }}
                className={styles.flexSizingTileEmerald}
              >
                flex: 1
              </div>
              <div
                style={{ flex: "2" }}
                className={styles.flexSizingTileCyan}
              >
                flex: 2
              </div>
              <div
                style={{ flex: "1" }}
                className={styles.flexSizingTileEmerald}
              >
                flex: 1
              </div>
            </div>
          </Section>
          <Section title="Item flex sizing with auto margin">
            <div className={styles.flexSizingRow}>
              <div
                style={{ flex: "0 1 40px" }}
                className={styles.flexSizingTileEmerald}
              >
                  40px
              </div>
              <div
                style={{ flex: "0 1 33%", margin: "auto" }}
                className={styles.flexSizingTileCyan}
              >
                margin: auto;
              </div>
              <div
                style={{flex: "0 1 40px"}}
                className={styles.flexSizingTileEmerald}
              >
                40px
              </div>
            </div>
          </Section>
        </div>
      </div>
    </CheatsheetCard>
  );
}
